import { ReactNode, useCallback } from 'react';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  DragHandle as DragIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
} from '@mui/icons-material';
import {
  useSortable,
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useConfirmation } from '@/hooks';

interface SortableItemProps {
  children: ReactNode;
  id: string;
  deleteConfirmation: boolean;
  onDelete?: () => void;
  onEdit?: () => void;
}

const SortableItem = ({
  children,
  id,
  onDelete,
  onEdit,
}: SortableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Stack
      ref={setNodeRef}
      sx={style}
      direction="row"
      flex={1}
      justifyContent="space-between"
      alignItems="center"
      spacing={2}
    >
      <Tooltip title="Reordenar" placement="top" arrow>
        <IconButton {...attributes} {...listeners}>
          <DragIcon />
        </IconButton>
      </Tooltip>

      <Typography component={'span'} flex={1}>
        {children}
      </Typography>

      {onEdit ? (
        <Tooltip title="Editar" placement="top" arrow>
          <IconButton onClick={onEdit}>
            <EditIcon />
          </IconButton>
        </Tooltip>
      ) : null}
      { onDelete ? (
        <Tooltip title="Eliminar" placement="top" arrow>
          <IconButton onClick={onDelete}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : null}
    </Stack>
  );
};

type Item<T> = {
  id: string;
  label: string;
} & T;

interface SortableListProps<T> {
  items: Item<T>[];
  deleteConfirmation?: boolean;
  onChange?: (items: Item<T>[]) => void;
  onEdit?: (item: Item<T>) => void;
  onDelete?: (items: Item<T>[], deletedItem: Item<T>) => void;
  renderItem?: (item: Item<T>, index: number) => ReactNode;
}

function SortableList<T>({
  items = [],
  deleteConfirmation = false,
  onEdit,
  onDelete,
  onChange,
  renderItem,
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDelete = useCallback(
    (itemId: string) => {
      const newItems = [...items];
      const index = newItems.findIndex(({ id }) => id === itemId);
      const deletedItem = newItems.splice(index, 1);
      onDelete?.(newItems, deletedItem[0]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items]
  );

  const [renderConfirmationDialog, confirmItemDeletion] = useConfirmation({
    title: 'Eliminar',
    message: 'Deseas eliminar este item?',
    dispatcher: handleDelete,
  });

  const handleDragEnd = (event: { active: any; over: any }) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = items.findIndex(({ id }) => id === active.id);
      const newIndex = items.findIndex(({ id }) => id === over.id);

      const newItems = arrayMove(items, oldIndex, newIndex);
      onChange?.(newItems);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {items.map((item, index) => (
          <SortableItem
            key={item.id}
            id={item.id}
            onDelete={onDelete ? (() =>
              deleteConfirmation
                ? confirmItemDeletion(item.id)
                : handleDelete(item.id)) : undefined
            }
            onEdit={onEdit ? () => onEdit?.(item) : undefined}
          >
            {renderItem ? renderItem(item, index) : item?.label}
          </SortableItem>
        ))}
      </SortableContext>
      {renderConfirmationDialog()}
    </DndContext>
  );
}

export default SortableList;
